import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { BaseSchemaModifierTransformation } from './base-schema-modifier-transformation';

import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { IValidationContext, IViolation, Owned, UniqueCaseSensitiveValidator } from 'shell-models-lib';
import { DataFlowSource } from './dataflow-source';
import { SelectTransformation } from './selectTransformation';
import { DataFlowColumn } from '../dataset/dataflowcolumn';
import { ColumnExpressionProcessor } from './column-expression-processor';
import { ComplexDataFlowType } from './dataflow-complex-datatype';
import { sanitizeAndFormatString } from 'app/shared/utils/string.util';
import { DataFlowExpression } from './dataflowexpression';

export interface ICallProperties {
     source: DataFlowSource;
     selectTx: SelectTransformation;
}

export enum OutputColumns {
    Body = 0,
    Header = 1,
    Status = 2
}

@Metamodel.model({
    name: 'call',
    referable: true,
    display: [stringResources.DataFlowCallTransformationLabel, stringResources.DataFlowCallTransformationsLabel],
    constraints: {
        name: {
            required: true
        }
    }
})
export class CallTransformation extends BaseSchemaModifierTransformation {
    public static readonly MODEL: IModel;

    public source: DataFlowSource;

    public selectTx: SelectTransformation;

    @Metamodel.attribute({
        constraint: {
            validator: CallTransformation._validateBodyColumn
        }
    })
    public staticBodyColumn: DataFlowColumn;
    public staticBodyDataFlowExpression: DataFlowExpression;

    @Metamodel.attribute({
        constraint: {
            validator: CallTransformation._validateHeaderColumn
        }
    })
    public headerColumn: DataFlowColumn;

    @Metamodel.attribute({
        constraint: {
            validator: CallTransformation._validateStatusColumn
        }
    })
    public statusColumn: DataFlowColumn;

    public includeHeader: boolean = false;
    public includeBody: boolean = false;
    public includeStatus: boolean = false;

    public isImportSchema: boolean = false;
    public previewRequest: boolean = false;

    public bodyColumnTypeParameter: string;

    public get isRestBasedSource(): boolean {
        return (this.source && this.source.datasetSettings && this.source.datasetSettings.getFormat() === DatasetFormatType.RestCall);
    }

    public constructor() {
        super();
        this.inspectSummaryOutputFields.splice(1, 0, {
            name: () => stringResources.TransformationInspect_Update,
            icon: () => 'svg-i-9x9-Update',
            value: () => this.overwrittenColumns ? this.overwrittenColumns.length : 0
        });
        this.inspectDetailsOutputFields.unshift({
            name: () => this.columnInfoTitle,
            icon: this._getUpdatedIcon.bind(this)
        });
        this.source = Owned.create<DataFlowSource>(DataFlowSource.MODEL, this);
        this.source.addOutputStream();
        this.staticBodyDataFlowExpression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, this);

        this.expressionColumns = [];
        this.overwrittenColumns = [];
        this.newColumns = [];
        this.unchangedColumns = [];
    }

    public get columns(): DataFlowColumn[] {
        this.expressionColumns = [];
        this.overwrittenColumns = [];
        this.newColumns = [];
        this.unchangedColumns = [];

        const callColumns: DataFlowColumn[] = ColumnExpressionProcessor.processNewColumns([], this.distinctInputColumnRefs, this).map((processedColumnResult) => processedColumnResult.column);

        if (this.isRestBasedSource) {
            // in REST source, we have one 'body' column
            if (this.includeBody && this.staticBodyDataFlowExpression.expression && this.staticBodyColumn) {
                callColumns.push(this.staticBodyColumn);
            }
            if (this.includeHeader && this.headerColumn) {
                callColumns.push(this.headerColumn);
            }
            if (this.includeStatus && this.statusColumn) {
                callColumns.push(this.statusColumn);
            }
        } else if (!this.isRestBasedSource && this.staticBodyDataFlowExpression.expression) {
            // in SQL based source, every field is considered a column
            const complexType = this.staticBodyDataFlowExpression.returnType as ComplexDataFlowType;
            if (!!complexType.fields) {
                // eslint-disable-next-line @typescript-eslint/prefer-for-of
                for (let i = 0; i < complexType.fields.length; i++) {
                    const field = complexType.fields[i];
                    const col = Owned.create<DataFlowColumn>(DataFlowColumn.MODEL, this);
                    col.dataFlowColumnType = field.dataFlowColumnType;
                    col.name = field.name;

                    callColumns.push(col);
                }
            }
        }

        const columns: DataFlowColumn[] = [];
        this.overwrittenColumns = [];
        this.newColumns = [];
        this.unchangedColumns = [];
        for (const column of this.inputColumns) {
            const overwrittenColumnIndex = callColumns.findIndex((callColumn) => (callColumn.name) === column.name);
            if (overwrittenColumnIndex > -1) {
                const overwrittenColumn = callColumns[overwrittenColumnIndex];
                if (columns.indexOf(overwrittenColumn) < 0) {
                    columns.push(overwrittenColumn);
                }
                if (this.overwrittenColumns.indexOf(overwrittenColumn) < 0) {
                    this.overwrittenColumns.push(overwrittenColumn);
                }
            } else {
                this.unchangedColumns.push(column);
                columns.push(column);
            }
        }

        for (const column of callColumns) {
            if (columns.indexOf(column) < 0) {
                columns.push(column);
                if (this.overwrittenColumns.indexOf(column) < 0) {
                    this.newColumns.push(column);
                }
            }
        }
        return columns;
    }

    private static validateColumn(callEntity: CallTransformation, column: DataFlowColumn, attribute: string, columnName: string, context: IValidationContext): IViolation[] | undefined {
        const violations: IViolation[] = [];

        if (column && !column.name) {
            violations.push({
                context: column,
                message: () => sanitizeAndFormatString(stringResources.DataFlowCallTransformColumnNameError, columnName)
            });
        }
        if (callEntity.columns) {
            const uniqueValidatorViolations = UniqueCaseSensitiveValidator.INSTANCE.validate(
                {},
                Metamodel.modelOf(callEntity).attribute(attribute),
                callEntity.columns, callEntity, context);
            if (uniqueValidatorViolations) {
                violations.push({
                    context: column,
                    message: () => sanitizeAndFormatString(stringResources.DataFlowCallTransformDuplicateColumnNameError, callEntity.name)
                });
            }
        }
        return violations;
    }

    private static _validateBodyColumn(callEntity: CallTransformation, column: DataFlowColumn, violation?: IViolation, context?: IValidationContext): IViolation[] | undefined {
        return callEntity.includeBody ? CallTransformation.validateColumn(callEntity, column, 'staticBodyColumn', 'Body', context) : undefined;
    }

    private static _validateHeaderColumn(callEntity: CallTransformation, column: DataFlowColumn, violation?: IViolation, context?: IValidationContext): IViolation[] | undefined {
        return callEntity.includeHeader ? CallTransformation.validateColumn(callEntity, column, 'headerColumn', 'Header', context) : undefined;
    }

    private static _validateStatusColumn(callEntity: CallTransformation, column: DataFlowColumn, violation?: IViolation, context?: IValidationContext): IViolation[] | undefined {
        return callEntity.includeStatus ? CallTransformation.validateColumn(callEntity, column, 'statusColumn', 'Status', context) : undefined;
    }

    protected getGeneratedDescription(): string {
        return stringResources.CallTransformationDescription;
    }
}
